// src/components/DayStatusManager.js
// ============================================================
// 本日狀態管理 — 掛在訂單管理頁頂部。Plan 117（前身：SoldoutManager）。
//   ① 本日下拉：開放中 / 全天·午餐·晚餐客滿 / 全天·午餐·晚餐店休（一鍵切目前選到的日期）。
//   ② 設定日期 dialog：月曆逐日點選 → 跳選單 → 立即生效。
// 客滿與店休寫的都是後端 constraint，線上訂位與 AI 電話同步停收。
//
// 為什麼要有「店休」：原本只給得起「客滿」，老闆要表達店休時手邊沒有別的選項，
//   只好拿客滿來用 → 客人聽到「本日已訂滿」（暗示還有機會、可以再打來問）
//   而不是「店休」（明確不營業）。連續 11 天全回「已訂滿」＝ 請客人連打 11 通。
//
// 區間／固定公休來源的日子（線上訂位設定建的）在這裡**看得到但改不動**：
//   在單日下拉上「恢復開放」一個區間中的日子，語意是把區間切成兩段，
//   那要有明確 UI，不能硬塞進下拉。後端對這種操作回 409。
//
// API：restaurantService.getDayStatus / setDayStatus / clearDayStatus。
// ============================================================
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Button, Select, MenuItem as SelectItem, ListSubheader, Dialog, DialogContent,
  Typography, IconButton, Menu, MenuItem, Divider, Alert, Chip, CircularProgress, Snackbar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import restaurantService from '../services/restaurantService';

const BLUE = '#145da0';
const RED = '#c62828';
const REST = '#546e7a';        // 店休＝中性灰，與客滿的紅在視覺上分開（語意差異，不是裝飾）
const WD = ['日', '一', '二', '三', '四', '五', '六'];

const LABEL = {
  soldout: { all: '全天客滿', lunch: '午餐客滿', dinner: '晚餐客滿' },
  closure: { all: '全天店休', lunch: '午餐店休', dinner: '晚上店休' }
};
const BADGE = {
  soldout: { all: '全', lunch: '午', dinner: '晚' },
  closure: { all: '休', lunch: '午休', dinner: '晚休' }
};
const TONE = {
  soldout: { fg: RED, bg: '#fdf3f3', line: '#f3c9c9', chip: '#fde7e7' },
  closure: { fg: REST, bg: '#eceff1', line: '#b0bec5', chip: '#dde3e7' }
};

const pad = (n) => String(n).padStart(2, '0');
const iso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
// 台北當地今天（與 RestaurantReservationsPage.todayYmd 同法，避免午夜邊界誤差）
const todayTaipei = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Taipei', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
const dowOf = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)).getUTCDay(); };
const shiftDays = (s, n) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};

// 下拉值編碼：'open' 或 '<status>:<meal>'
const encode = (it) => (it ? `${it.status}:${it.meal}` : 'open');
const decode = (v) => (v === 'open' ? null : { status: v.split(':')[0], meal: v.split(':')[1] });

// selectedDate：訂單管理頁上方日期導覽選到的日期（YYYY-MM-DD）→ 下拉綁「這一天」。
// fz：字級縮放函式（跟隨全域「A」），由頁面傳入（預設不縮放）。
const DayStatusManager = ({ selectedDate, fz = (x) => x, isMobile = false }) => {
  const TODAY = todayTaipei();
  const activeDate = (selectedDate && DATE_RE.test(selectedDate)) ? selectedDate : TODAY;
  const isPast = activeDate < TODAY;

  const [map, setMap] = useState({});          // date → item
  // 一次載入夠寬的窗（過去 30 天 ~ 未來 360 天，後端上限 400 天）。
  // 月曆翻到窗外時由 mv() 補撈該月並 merge，所以這個窗本身不需要變動。
  const win = useMemo(() => ({ from: shiftDays(TODAY, -30), to: shiftDays(TODAY, 360) }), [TODAY]);
  const [loading, setLoading] = useState(false);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [viewY, setViewY] = useState(() => parseInt(TODAY.slice(0, 4), 10));
  const [viewM, setViewM] = useState(() => parseInt(TODAY.slice(5, 7), 10) - 1);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuDate, setMenuDate] = useState(null);
  const [toast, setToast] = useState(null);    // { severity, text }

  // 撈一個窗，合併進 map（月曆翻到窗外時會再撈那個月）
  const load = useCallback(async (from, to, replace = false) => {
    setLoading(true);
    try {
      const body = await restaurantService.getDayStatus(from, to);
      const items = Array.isArray(body?.items) ? body.items : [];
      setMap((prev) => {
        const next = replace ? {} : { ...prev };
        // 這個窗內的舊值先清掉，才不會留下已被取消的狀態
        Object.keys(next).forEach((d) => { if (d >= from && d <= to) delete next[d]; });
        items.forEach((it) => { next[it.date] = it; });
        return next;
      });
    } catch (e) { /* noop：讀取失敗維持既有顯示，不要把畫面清空 */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(win.from, win.to, true); }, [load, win.from, win.to]);

  const active = map[activeDate] || null;
  const locked = !!(active && active.editableHere === false);

  const apply = async (date, value) => {
    const parsed = decode(value);
    const body = parsed
      ? await restaurantService.setDayStatus(date, parsed.status, parsed.meal)
      : await restaurantService.clearDayStatus(date);

    if (body?.success === false) {
      setToast({
        severity: body.error === 'CONFLICT' ? 'info' : 'error',
        text: body.message || '設定失敗，請稍後再試'
      });
    } else {
      setToast({ severity: 'success', text: parsed ? `已設為${LABEL[parsed.status][parsed.meal]}` : '已恢復開放' });
    }
    await load(win.from, win.to);
  };

  const onActiveChange = async (e) => { await apply(activeDate, e.target.value); };

  const openDlg = () => {
    setViewY(parseInt(activeDate.slice(0, 4), 10));
    setViewM(parseInt(activeDate.slice(5, 7), 10) - 1);
    setDlgOpen(true);
  };

  const openDayMenu = (e, date) => { setMenuAnchor(e.currentTarget); setMenuDate(date); };
  const closeMenu = () => { setMenuAnchor(null); setMenuDate(null); };
  const pick = async (value) => { const d = menuDate; closeMenu(); await apply(d, value); };

  // 翻月；翻到已載入窗之外就補撈那個月
  const mv = (n) => {
    let m = viewM + n; let y = viewY;
    if (m < 0) { m = 11; y -= 1; } if (m > 11) { m = 0; y += 1; }
    setViewM(m); setViewY(y);
    const from = iso(y, m, 1);
    const to = iso(y, m, new Date(Date.UTC(y, m + 1, 0)).getUTCDate());
    if (from < win.from || to > win.to) load(from, to);
  };

  const renderGrid = () => {
    const first = new Date(Date.UTC(viewY, viewM, 1)).getUTCDay();
    const dim = new Date(Date.UTC(viewY, viewM + 1, 0)).getUTCDate();
    const cells = [];
    for (let i = 0; i < first; i++) cells.push(<Box key={`e${i}`} />);
    for (let d = 1; d <= dim; d++) {
      const s = iso(viewY, viewM, d);
      const past = s < TODAY;
      const isToday = s === TODAY;
      const it = map[s];
      const tone = it ? TONE[it.status] : null;
      const cellLocked = !!(it && it.editableHere === false);
      const clickable = !past && !cellLocked;
      cells.push(
        <Box key={s}
          onClick={clickable ? (e) => openDayMenu(e, s) : undefined}
          title={it ? (it.label || '') : ''}
          sx={{
            aspectRatio: '1 / 1', borderRadius: 2, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', fontSize: 15, position: 'relative',
            cursor: clickable ? 'pointer' : 'default',
            color: past ? '#c4ccd4' : '#33404c',
            bgcolor: past ? '#fafbfc' : (tone ? tone.bg : '#fff'),
            opacity: cellLocked && !past ? 0.75 : 1,
            border: '1px solid',
            borderColor: isToday ? BLUE : (tone ? tone.line : '#eceff3'),
            borderStyle: cellLocked ? 'dashed' : 'solid',
            fontWeight: isToday ? 800 : 400,
            '&:hover': clickable ? { borderColor: BLUE, boxShadow: `0 0 0 2px rgba(20,93,160,.12)` } : {}
          }}>
          <span>{d}</span>
          {it && (
            <Box sx={{ mt: '2px', fontSize: 10, fontWeight: 800, px: '4px', borderRadius: '5px', bgcolor: tone.chip, color: tone.fg }}>
              {BADGE[it.status][it.meal]}
            </Box>
          )}
          {cellLocked && !past && (
            <LockIcon sx={{ position: 'absolute', top: 2, right: 3, fontSize: 10, color: REST }} />
          )}
        </Box>
      );
    }
    return cells;
  };

  const tone = active ? TONE[active.status] : null;
  const dropSx = {
    fontSize: fz(14), fontWeight: 700, borderRadius: 2,
    color: active ? tone.fg : BLUE,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: active ? tone.line : '#d5dbe3' },
    bgcolor: active ? tone.bg : '#fff', minWidth: fz(isMobile ? 78 : 132)
  };

  // 可在此編輯的單日設定（dialog 清單用）；區間／固定公休另外列，不給刪
  const dayItems = Object.values(map).filter((it) => it.editableHere && it.date >= TODAY).sort((a, b) => a.date.localeCompare(b.date));
  const lockedItems = Object.values(map).filter((it) => !it.editableHere && it.date >= TODAY).sort((a, b) => a.date.localeCompare(b.date));

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: (!active || isPast) ? '#c4ccd4' : tone.fg, flexShrink: 0 }} />
      <Select size="small" value={encode(active)} onChange={onActiveChange} sx={dropSx} disabled={isPast || locked}>
        <SelectItem value="open">開放中</SelectItem>
        <ListSubheader sx={{ fontSize: 11.5, lineHeight: '28px', color: '#93a0ac' }}>客滿（訂位額滿）</ListSubheader>
        <SelectItem value="soldout:all">全天客滿</SelectItem>
        <SelectItem value="soldout:lunch">午餐客滿</SelectItem>
        <SelectItem value="soldout:dinner">晚餐客滿</SelectItem>
        <ListSubheader sx={{ fontSize: 11.5, lineHeight: '28px', color: '#93a0ac' }}>店休（不營業）</ListSubheader>
        <SelectItem value="closure:all">全天店休</SelectItem>
        <SelectItem value="closure:lunch">午餐店休</SelectItem>
        <SelectItem value="closure:dinner">晚上店休</SelectItem>
      </Select>

      {locked && !isPast && (
        <Typography sx={{ fontSize: fz(12), color: '#7d8894', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <LockIcon sx={{ fontSize: fz(13) }} />
          {isMobile ? '設定頁管理' : `由「${active.sourceName}」設定`}
          <Box component="a" href="/web-booking-settings" sx={{ color: BLUE, fontWeight: 700, textDecoration: 'none' }}>調整</Box>
        </Typography>
      )}
      {isPast && !isMobile && <Typography sx={{ fontSize: fz(12), color: '#9aa5b1' }}>過去日期不可設定</Typography>}

      <Button variant="contained" size="small" startIcon={isMobile ? undefined : <EventBusyIcon />} onClick={openDlg}
        sx={{ textTransform: 'none', bgcolor: BLUE, fontWeight: 700, fontSize: fz(13.5), whiteSpace: 'nowrap', px: isMobile ? 1.25 : 2, '&:hover': { bgcolor: '#0e447a' } }}>
        設定日期
      </Button>

      {/* 設定日期 dialog（逐日點選） */}
      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ px: 2.5, pt: 2, pb: 1.5, borderBottom: '1px solid #eef1f5', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0f2b4a' }}>設定客滿／店休日期</Typography>
            <Typography sx={{ fontSize: 12.5, color: '#6b7280', mt: 0.5 }}>
              點日期 → 選客滿或店休（點已設定的可改或取消）。線上訂位＋AI 電話同步。
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setDlgOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </Box>
        <DialogContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <IconButton size="small" onClick={() => mv(-1)}><ChevronLeftIcon /></IconButton>
            <Typography sx={{ fontWeight: 800, color: '#0f2b4a' }}>{viewY} 年 {viewM + 1} 月</Typography>
            <IconButton size="small" onClick={() => mv(1)}><ChevronRightIcon /></IconButton>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 0.5 }}>
            {WD.map((w) => <Box key={w} sx={{ textAlign: 'center', fontSize: 12, color: '#93a0ac', py: 0.5 }}>{w}</Box>)}
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
            {renderGrid()}
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, flexWrap: 'wrap', fontSize: 12, color: '#6b7280' }}>
            <span><Box component="i" sx={{ display: 'inline-block', width: 12, height: 12, borderRadius: '4px', bgcolor: TONE.soldout.bg, border: `1px solid ${TONE.soldout.line}`, verticalAlign: '-2px', mr: 0.5 }} />客滿（全/午/晚）</span>
            <span><Box component="i" sx={{ display: 'inline-block', width: 12, height: 12, borderRadius: '4px', bgcolor: TONE.closure.bg, border: `1px solid ${TONE.closure.line}`, verticalAlign: '-2px', mr: 0.5 }} />店休（休/午休/晚休）</span>
            <span><LockIcon sx={{ fontSize: 12, color: REST, verticalAlign: '-2px', mr: 0.5 }} />設定頁管理，此處不可改</span>
          </Box>

          <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0f2b4a', mt: 2.5, mb: 1 }}>本頁設定的日期</Typography>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={20} /></Box>
          ) : dayItems.length === 0 ? (
            <Box sx={{ p: 1.5, textAlign: 'center', fontSize: 13, color: '#9aa5b1', border: '1px dashed #e2e8ee', borderRadius: 2 }}>
              目前沒有設定 — 點上方月曆的日期即可設定
            </Box>
          ) : (
            dayItems.map((it) => (
              <Box key={it.date} sx={{ display: 'flex', alignItems: 'center', gap: 1, border: '1px solid #eef1f5', borderRadius: 2, px: 1.5, py: 1, mb: 1 }}>
                <Typography sx={{ fontWeight: 800, color: '#0f2b4a', fontSize: 14 }}>
                  {it.date.slice(5).replace('-', '/')}（{WD[dowOf(it.date)]}）
                </Typography>
                <Chip size="small" label={LABEL[it.status][it.meal]}
                  sx={{ bgcolor: TONE[it.status].chip, color: TONE[it.status].fg, fontWeight: 700 }} />
                <Typography sx={{ ml: 'auto', fontSize: 11, color: '#9aa5b1' }}>🌐 線上 · 📞 AI 電話 皆停收</Typography>
                <IconButton size="small" onClick={() => apply(it.date, 'open')}><CloseIcon fontSize="small" /></IconButton>
              </Box>
            ))
          )}

          {lockedItems.length > 0 && (
            <>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0f2b4a', mt: 2.5, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LockIcon sx={{ fontSize: 14, color: REST }} />由「線上訂位設定」管理
              </Typography>
              {lockedItems.slice(0, 12).map((it) => (
                <Box key={it.date} sx={{ display: 'flex', alignItems: 'center', gap: 1, border: '1px dashed #d5dbe3', borderRadius: 2, px: 1.5, py: 0.75, mb: 0.75, opacity: 0.85 }}>
                  <Typography sx={{ fontWeight: 700, color: '#4a5b6b', fontSize: 13.5 }}>
                    {it.date.slice(5).replace('-', '/')}（{WD[dowOf(it.date)]}）
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: '#6b7280' }}>{it.label}</Typography>
                </Box>
              ))}
              {lockedItems.length > 12 && (
                <Typography sx={{ fontSize: 12, color: '#9aa5b1', mb: 1 }}>…另有 {lockedItems.length - 12} 天</Typography>
              )}
              <Button size="small" href="/web-booking-settings"
                sx={{ textTransform: 'none', fontWeight: 700, color: BLUE, px: 0 }}>
                前往線上訂位設定調整 →
              </Button>
            </>
          )}

          <Alert severity="warning" sx={{ mt: 1.5, fontSize: 12.5, borderRadius: 2 }}>
            <b>客滿</b>會告訴客人「本時段已訂滿」；<b>店休</b>會告訴客人「本日店休」。
            兩者線上訂位與 AI 電話都會停收，但講法不同 —— 出國、休假請用店休。
          </Alert>
        </DialogContent>
        <Box sx={{ p: 2, borderTop: '1px solid #eef1f5', display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={() => setDlgOpen(false)}
            sx={{ textTransform: 'none', bgcolor: BLUE, fontWeight: 700, '&:hover': { bgcolor: '#0e447a' } }}>完成</Button>
        </Box>
      </Dialog>

      {/* 日期點選 → 狀態選單（桌機/手機共用） */}
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}>
        {menuDate && (
          <Typography sx={{ px: 2, py: 1, fontSize: 13, fontWeight: 800, color: '#0f2b4a' }}>
            {menuDate.slice(5).replace('-', '/')}（{WD[dowOf(menuDate)]}）
          </Typography>
        )}
        <ListSubheader sx={{ fontSize: 11.5, lineHeight: '26px', color: '#93a0ac' }}>客滿（訂位額滿）</ListSubheader>
        <MenuItem onClick={() => pick('soldout:all')} selected={menuDate && encode(map[menuDate]) === 'soldout:all'}>全天客滿</MenuItem>
        <MenuItem onClick={() => pick('soldout:lunch')} selected={menuDate && encode(map[menuDate]) === 'soldout:lunch'}>僅午餐客滿</MenuItem>
        <MenuItem onClick={() => pick('soldout:dinner')} selected={menuDate && encode(map[menuDate]) === 'soldout:dinner'}>僅晚餐客滿</MenuItem>
        <ListSubheader sx={{ fontSize: 11.5, lineHeight: '26px', color: '#93a0ac' }}>店休（不營業）</ListSubheader>
        <MenuItem onClick={() => pick('closure:all')} selected={menuDate && encode(map[menuDate]) === 'closure:all'}>全天店休</MenuItem>
        <MenuItem onClick={() => pick('closure:lunch')} selected={menuDate && encode(map[menuDate]) === 'closure:lunch'}>僅午餐店休</MenuItem>
        <MenuItem onClick={() => pick('closure:dinner')} selected={menuDate && encode(map[menuDate]) === 'closure:dinner'}>僅晚上店休</MenuItem>
        {menuDate && map[menuDate] && <Divider />}
        {menuDate && map[menuDate] && (
          <MenuItem onClick={() => pick('open')} sx={{ color: BLUE, fontWeight: 700 }}>↩︎ 恢復開放</MenuItem>
        )}
      </Menu>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? (
          <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)} sx={{ fontSize: 13.5 }}>
            {toast.text}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default DayStatusManager;
